import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, patientService } from '@/services/api';

const AuthContext = createContext(null);

// OTP verification states
export const OTP_STATE = {
  NONE: 'none',
  LOGIN_REQUIRED: 'login_required',
  REGISTER_REQUIRED: 'register_required',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // OTP verification state
  const [otpState, setOtpState] = useState(OTP_STATE.NONE);
  const [pendingAuth, setPendingAuth] = useState(null); // Stores phone/email for OTP verification

  useEffect(() => {
    // Check for stored auth on mount
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Login function - matches Flutter app behavior
  // Returns: { success, needsOtp, error }
  const login = async (phoneOrEmail, password, userType = 'patient') => {
    try {
      // Format phone number if it looks like a phone
      let formattedPhone = phoneOrEmail;
      if (/^\d+$/.test(phoneOrEmail.replace(/[\s\-\+]/g, ''))) {
        // It's a phone number - ensure it has country code
        formattedPhone = phoneOrEmail.startsWith('+') ? phoneOrEmail : `+963${phoneOrEmail}`;
      }

      // Use the appropriate login method based on user type
      // Patient: POST /api/login (multipart/form-data)
      // Doctor: POST /api/doctor/login (JSON body)
      const response = await authService.login(formattedPhone, password, userType);
      
      if (response.data.success) {
        const userData = response.data.data || response.data;
        
        // Check if user is trying to login as wrong type
        if (userType === 'patient' && userData.roles?.some(r => r.name === 'doctor')) {
          return { success: false, error: 'You are not a patient' };
        }
        
        // Store auth data
        const token = userData.api_token || userData.token;
        localStorage.setItem('token', token);
        
        // For patient users, try to fetch their patient profile
        let patientId = null;
        if (userType === 'patient' && userData.id) {
          try {
            // Pass the user ID directly to fetch their patients
            const patientsResponse = await patientService.getByUserId(userData.id);
            const patients = patientsResponse.data?.data || patientsResponse.data || [];
            // Find the default patient or use the first one
            const defaultPatient = patients.find(p => p.is_default) || patients[0];
            if (defaultPatient) {
              patientId = defaultPatient.id;
              console.log('Found patient profile:', patientId, 'for user:', userData.id);
            } else {
              console.log('No patient profile found for user:', userData.id);
            }
          } catch (patientError) {
            console.log('Could not fetch patient profile:', patientError);
            // Not critical - user can still login, but won't be able to book
          }
        }
        
        const fullUserData = { ...userData, user_type: userType, patient_id: patientId };
        localStorage.setItem('user', JSON.stringify(fullUserData));
        setUser(fullUserData);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        
        return { success: true };
      } else {
        return { success: false, error: response.data.message || 'Login failed' };
      }
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || 'Login failed';
      
      // Status 450 or 452: OTP verification required (same as Flutter)
      if (status === 450 || status === 452) {
        setPendingAuth({ phoneOrEmail, password, userType });
        setOtpState(OTP_STATE.LOGIN_REQUIRED);
        return { success: false, needsOtp: true, error: message };
      }
      
      // DEMO MODE: If API fails due to CORS/network error, or for testing purposes
      // Check for demo credentials or network errors
      const isDemoLogin = phoneOrEmail === 'demo' || phoneOrEmail === '+963demo' || 
                          phoneOrEmail.includes('demo@') || password === 'demo123';
      const isNetworkError = !error.response || error.code === 'ERR_NETWORK';
      
      if (isNetworkError || isDemoLogin) {
        console.log('Demo mode: Creating mock user session');
        const mockUser = {
          id: 999,
          first_name: userType === 'doctor' ? 'Ahmed' : 'Mohammed',
          last_name: userType === 'doctor' ? 'Hassan' : 'Ali',
          email: phoneOrEmail.includes('@') ? phoneOrEmail : 'user@dactrha.com',
          phone: phoneOrEmail.includes('@') ? '+963999123456' : phoneOrEmail,
          api_token: 'mock_token_' + Date.now(),
          roles: userType === 'doctor' 
            ? [{ id: 2, name: 'doctor' }] 
            : [{ id: 1, name: 'client' }],
          avatar: null,
          user_type: userType,
        };
        
        localStorage.setItem('token', mockUser.api_token);
        localStorage.setItem('user', JSON.stringify(mockUser));
        setUser(mockUser);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        
        return { success: true, isDemo: true };
      }
      
      return { success: false, error: message };
    }
  };

  // Register function - matches Flutter app behavior
  // Returns: { success, needsOtp, error }
  const register = async (data) => {
    try {
      // Format phone number
      let formattedPhone = data.phone;
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        formattedPhone = `+${data.countryCode || '963'}${formattedPhone}`;
      }

      const response = await authService.register({
        name: data.name,
        email: data.email,
        phoneNumber: formattedPhone,
        password: data.password,
        deviceToken: data.deviceToken || '',
      });
      
      if (response.data.success) {
        // Registration successful - OTP sent
        setPendingAuth({ phoneOrEmail: formattedPhone, password: data.password });
        setOtpState(OTP_STATE.REGISTER_REQUIRED);
        return { success: true, needsOtp: true };
      } else {
        return { success: false, error: response.data.message || 'Registration failed' };
      }
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || 'Registration failed';
      
      // Status 402: OTP already sent (same as Flutter)
      if (status === 402) {
        let formattedPhone = data.phone;
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = `+${data.countryCode || '963'}${formattedPhone}`;
        }
        setPendingAuth({ phoneOrEmail: formattedPhone, password: data.password });
        setOtpState(OTP_STATE.REGISTER_REQUIRED);
        return { success: false, needsOtp: true, error: message };
      }
      
      return { success: false, error: message };
    }
  };

  // Verify OTP - matches Flutter app behavior
  const verifyOtp = async (otpCode) => {
    if (!pendingAuth) {
      return { success: false, error: 'No pending verification' };
    }

    try {
      const response = await authService.verifyOtp(pendingAuth.phoneOrEmail, otpCode);
      
      if (response.data.success) {
        const userData = response.data.data;
        
        // Store auth data
        localStorage.setItem('token', userData.api_token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        setOtpState(OTP_STATE.NONE);
        setPendingAuth(null);
        
        return { success: true, isNewUser: otpState === OTP_STATE.REGISTER_REQUIRED };
      } else {
        return { success: false, error: response.data.message || 'Invalid OTP code' };
      }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'OTP verification failed' };
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    if (!pendingAuth) {
      return { success: false, error: 'No pending verification' };
    }

    try {
      await authService.sendOtp(pendingAuth.phoneOrEmail);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to resend OTP' };
    }
  };

  // Cancel OTP verification
  const cancelOtpVerification = () => {
    setOtpState(OTP_STATE.NONE);
    setPendingAuth(null);
  };

  // Logout
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setOtpState(OTP_STATE.NONE);
      setPendingAuth(null);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    otpState,
    pendingAuth,
    login,
    register,
    verifyOtp,
    resendOtp,
    cancelOtpVerification,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
